import {
  CommissionSummary,
  StatisticsComputationMode,
  StatisticsRow,
  StatisticsSummary,
} from './commission-summary.model';

/** 1 = Split Member/Leader, 0 = 100% Team Member */
const SPLIT_MEMBER_LEADER: StatisticsComputationMode = 1;

type CommissionSide = CommissionSummary['seller'] | CommissionSummary['buyer'];

/**
 * Calcula lo que suma cada persona a su estadistica de cierres, en dolares.
 * Replica el calculo que hace el backend al procesar la rendicion, con los
 * datos que ya tiene el resumen de comisiones del formulario.
 * No interviene en la liquidacion: el modo solo cambia la estadistica.
 */
export function buildStatisticsSummary(
  summary: CommissionSummary,
  mode: StatisticsComputationMode,
  exchangeRate: number,
): StatisticsSummary {
  const AMOUNTS = new Map<string, number>();

  // La seccion muestra los montos solo en dolares, asi que la parte en moneda
  // local se pasa a dolares con el tipo de cambio de la rendicion
  const toUSD = (amount?: { usd: number; local: number }): number => {
    const USD = amount?.usd ?? 0;
    const LOCAL = amount?.local ?? 0;
    return USD + (exchangeRate > 0 ? LOCAL / exchangeRate : 0);
  };

  // Una persona puede tener varios roles en la misma operacion
  const add = (name: string | null | undefined, usd: number): void => {
    if (!name) return;
    AMOUNTS.set(name, (AMOUNTS.get(name) ?? 0) + usd);
  };

  const isOwnSide = (side: CommissionSide): boolean =>
    !side.officeId && !side.otherRealEstateName;

  const SPLITS_WITH_TEAM_LEADER = mode === SPLIT_MEMBER_LEADER;

  // El referido solo se le descuenta al agente si la operacion no tiene cobrokers
  const HAS_COBROKERS = (summary.cobrokers ?? []).length > 0;

  const addSide = (side: CommissionSide): void => {
    // Agente de la punta
    if (side.agent.name && isOwnSide(side)) {
      add(
        side.agent.name,
        toUSD(side) -
          (SPLITS_WITH_TEAM_LEADER ? toUSD(side.teamLeader.rawCommission) : 0) -
          (HAS_COBROKERS ? 0 : toUSD(side.referred.rawCommission)),
      );
    }

    // Team Leader de la punta
    if (side.teamLeader.name && SPLITS_WITH_TEAM_LEADER) {
      add(side.teamLeader.name, toUSD(side.teamLeader.rawCommission));
    }

    // Referido de la punta
    if (side.referred.name && !side.referred.officeId) {
      add(side.referred.name, toUSD(side.referred.rawCommission));
    }
  };

  addSide(summary.seller);
  addSide(summary.buyer);

  // Cobrokers: solo los de mi oficina suman estadistica
  for (const cobroker of summary.cobrokers ?? []) {
    if (cobroker.officeId || !cobroker.office) continue;

    const TEAM_LEADER_USD =
      cobroker.teamLeader?.name && SPLITS_WITH_TEAM_LEADER
        ? toUSD(cobroker.teamLeader.rawCommission)
        : 0;

    add(cobroker.name, toUSD(cobroker.rawCommission) - TEAM_LEADER_USD);
    add(cobroker.teamLeader?.name, TEAM_LEADER_USD);
  }

  const ROWS: StatisticsRow[] = Array.from(AMOUNTS, ([name, usd]) => ({
    name,
    usd,
  }))
    .filter((row) => Math.round(row.usd * 100) !== 0)
    .sort((a, b) => b.usd - a.usd);

  return {
    mode,
    rows: ROWS,
    total: ROWS.reduce((sum, row) => sum + row.usd, 0),
  };
}
